/**
 * Activity Tracker Composable
 * Tracks user navigation and actions, sends to backend
 */
import { ref, onMounted, onUnmounted, watch } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import apiClient from '@/api/client'
import { isTechPreview } from '@/preview/techPreview'

// Session ID for grouping activities
let sessionId = null

// Generate or get session ID
function getSessionId() {
  if (!sessionId) {
    sessionId = localStorage.getItem('activity_session_id')
    if (!sessionId) {
      sessionId = `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
      localStorage.setItem('activity_session_id', sessionId)
    }
  }
  return sessionId
}

// Track queue for batching
const trackQueue = []
let flushTimeout = null

/**
 * Flush the track queue to backend
 */
async function flushTrackQueue() {
  if (trackQueue.length === 0) return
  
  const items = [...trackQueue]
  trackQueue.length = 0
  
  // Send each item (could be optimized with batch endpoint later)
  for (const item of items) {
    try {
      await apiClient.post('/activity/track/', item, {
        headers: {
          'X-Session-ID': getSessionId()
        }
      })
    } catch (error) {
      // Don't break on tracking errors - silent fail
      console.debug('[ActivityTracker] Failed to track:', error.message)
    }
  }
}

/**
 * Queue an activity for tracking
 */
function queueTrack(data) {
  // Tech App Preview is a simulator: don't write activity events (read-only
  // tokens would get 403 anyway; live mode must not pollute the tech's log).
  if (isTechPreview()) return
  trackQueue.push({
    ...data,
    session_id: getSessionId(),
    timestamp: new Date().toISOString()
  })
  
  // Debounce flush
  if (flushTimeout) {
    clearTimeout(flushTimeout)
  }
  flushTimeout = setTimeout(flushTrackQueue, 2000)
}

/**
 * Main composable for activity tracking
 */
export function useActivityTracker() {
  const router = useRouter()
  const route = useRoute()
  const authStore = useAuthStore()
  
  // Track page navigation
  function trackNavigation(to, from) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'navigate',
      page_path: to.path,
      page_title: to.meta?.title || to.name || 'Unknown',
      description: `Navigated to ${to.meta?.title || to.path}`,
      extra_data: {
        from_path: from?.path,
        from_title: from?.meta?.title
      }
    })
  }
  
  // Track click events
  function trackClick(elementName, extraData = {}) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'click',
      entity_type: 'ui_element',
      entity_name: elementName,
      page_path: route.path,
      page_title: route.meta?.title,
      description: `Clicked on ${elementName}`,
      extra_data: extraData
    })
  }
  
  // Track view of entity
  function trackView(entityType, entityId = null, entityName = null, extraData = {}) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'view',
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      page_path: route.path,
      page_title: route.meta?.title,
      description: entityName ? `Viewed ${entityType}: ${entityName}` : `Viewed ${entityType}`,
      extra_data: extraData
    })
  }
  
  // Track create action
  function trackCreate(entityType, entityId, entityName = null, extraData = {}) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'create',
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      page_path: route.path,
      page_title: route.meta?.title,
      description: entityName ? `Created ${entityType}: ${entityName}` : `Created ${entityType}`,
      extra_data: extraData
    })
  }
  
  // Track update action
  function trackUpdate(entityType, entityId, entityName = null, changes = {}) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'update',
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      page_path: route.path,
      page_title: route.meta?.title,
      description: entityName ? `Updated ${entityType}: ${entityName}` : `Updated ${entityType}`,
      extra_data: { changes }
    })
  }
  
  // Track delete action
  function trackDelete(entityType, entityId, entityName = null) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'delete',
      entity_type: entityType,
      entity_id: entityId,
      entity_name: entityName,
      page_path: route.path,
      page_title: route.meta?.title,
      description: entityName ? `Deleted ${entityType}: ${entityName}` : `Deleted ${entityType}`
    })
  }
  
  // Track search
  function trackSearch(query, entityType = null, resultsCount = null) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'search',
      entity_type: entityType || 'search',
      page_path: route.path,
      page_title: route.meta?.title,
      description: `Searched for: ${query}`,
      extra_data: { query, results_count: resultsCount }
    })
  }
  
  // Track export
  function trackExport(entityType, format = 'unknown', count = null) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: 'export',
      entity_type: entityType,
      page_path: route.path,
      page_title: route.meta?.title,
      description: `Exported ${entityType} as ${format}`,
      extra_data: { format, count }
    })
  }
  
  // Track custom action
  function trackAction(actionType, description, extraData = {}) {
    if (!authStore.isAuthenticated) return
    
    queueTrack({
      action_type: actionType,
      page_path: route.path,
      page_title: route.meta?.title,
      description: description,
      extra_data: extraData
    })
  }
  
  return {
    trackNavigation,
    trackClick,
    trackView,
    trackCreate,
    trackUpdate,
    trackDelete,
    trackSearch,
    trackExport,
    trackAction,
    getSessionId
  }
}

/**
 * Plugin to automatically track navigation
 * Add to main.js: app.use(ActivityTrackerPlugin)
 */
export const ActivityTrackerPlugin = {
  install(app) {
    const router = app.config.globalProperties.$router
    
    if (router) {
      router.afterEach((to, from) => {
        // Skip if user not authenticated
        const authStore = useAuthStore()
        if (!authStore.isAuthenticated) return
        
        // Track navigation
        queueTrack({
          action_type: 'navigate',
          page_path: to.path,
          page_title: to.meta?.title || to.name || 'Unknown',
          description: `Navigated to ${to.meta?.title || to.path}`,
          session_id: getSessionId(),
          extra_data: {
            from_path: from?.path,
            from_title: from?.meta?.title
          }
        })
      })
    }
    
    // Flush on page unload
    window.addEventListener('beforeunload', () => {
      flushTrackQueue()
    })
    
    // Flush on visibility change (tab switch)
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'hidden') {
        flushTrackQueue()
      }
    })
  }
}

export default useActivityTracker




